import React, { useRef, useState } from 'react';
import { Download, XCircle, Palette } from 'lucide-react';
import { exportDocument, ExportFormat } from '../utils/documentExport';
import { exportProfessionalDocument, ProfessionalExportFormat } from '../utils/professionalDocumentExport';
import ReportTemplateSelector, { ReportTemplate } from './ReportTemplateSelector';
import ReportCustomizer, { ReportCustomizations } from './ReportCustomizer';
import { toast } from 'react-hot-toast';

interface ReportViewerProps {
  title: string;
  content: string;
  onClose: () => void;
}

const ReportViewer: React.FC<ReportViewerProps> = ({ title, content, onClose }) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('pdf');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [customizations, setCustomizations] = useState<ReportCustomizations | null>(null);

  const handleBasicDownload = async () => {
    if (reportRef.current) {
      const fileName = `${title.toLowerCase().replace(/\s+/g, '-')}.${exportFormat}`;
      await exportDocument(reportRef.current, content, exportFormat, fileName);
    }
  };

  const handleProfessionalFormat = () => {
    setShowTemplateSelector(true);
  };

  const handleTemplateSelect = (template: ReportTemplate) => {
    setSelectedTemplate(template);
    setShowTemplateSelector(false);
    setShowCustomizer(true);
  };

  const handleCustomize = (newCustomizations: ReportCustomizations) => {
    setCustomizations(newCustomizations);
  };

  const handleProfessionalExport = async (format: ProfessionalExportFormat) => {
    if (!selectedTemplate || !customizations) {
      toast.error('Please select a template and customize your report first');
      return;
    }

    try {
      const fileName = `${title.toLowerCase().replace(/\s+/g, '-')}-professional.${format}`;
      await exportProfessionalDocument(
        {
          template: selectedTemplate,
          customizations,
          title,
          content
        },
        format,
        fileName
      );
      toast.success(`Professional ${format.toUpperCase()} exported successfully!`);
      setShowCustomizer(false);
    } catch (error) {
      console.error('Error exporting professional document:', error);
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const formatContentForDisplay = (content: string) => {
    return content.split('\n').map((paragraph, index) => {
      // Handle main section headers (SECTION X: TITLE)
      if (paragraph.match(/^SECTION \d+:/)) {
        return <h2 key={index} className="text-xl font-bold mt-8 mb-4 text-primary-800 border-b-2 border-primary-200 pb-2">{paragraph}</h2>;
      }
      
      // Handle subsection headers (Title Case without special formatting)
      if (paragraph.match(/^[A-Z][a-zA-Z\s]+:$/) && paragraph.length < 50) {
        return <h3 key={index} className="text-lg font-semibold mt-6 mb-3 text-primary-700">{paragraph}</h3>;
      }
      
      // Handle numbered lists
      if (paragraph.match(/^\d+\./)) {
        return <div key={index} className="ml-6 mb-2 text-neutral-700">{paragraph}</div>;
      }
      
      // Handle bullet points with dashes
      if (paragraph.startsWith('- ')) {
        return <div key={index} className="ml-6 mb-2 text-neutral-700 before:content-['•'] before:mr-2 before:text-primary-600">{paragraph.substring(2)}</div>;
      }
      
      // Regular paragraph
      return paragraph.trim() ? <p key={index} className="mb-4 text-neutral-800 leading-relaxed">{paragraph}</p> : <br key={index} />;
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold text-primary-800">{title}</h2>
          <div className="flex space-x-2">
            <button
              onClick={handleProfessionalFormat}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2"
              title="Professional Formatting"
            >
              <Palette className="h-4 w-4" />
              Professional Format
            </button>
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
              className="px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="pdf">PDF</option>
              <option value="docx">DOCX</option>
            </select>
            <button
              onClick={handleBasicDownload}
              className="p-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              title={`Download as ${exportFormat.toUpperCase()}`}
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-neutral-500 hover:text-neutral-700 transition-colors"
              title="Close"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6 overflow-y-auto flex-grow">
          <div 
            ref={reportRef}
            className="bg-white p-8 report-content"
          >
            <h1 className="text-2xl font-bold mb-6 text-primary-800 border-b-3 border-primary-300 pb-3">{title}</h1>
            <div className="prose max-w-none">
              {formatContentForDisplay(content)}
            </div>
          </div>
        </div>
      </div>

      {showTemplateSelector && (
        <ReportTemplateSelector
          onSelectTemplate={handleTemplateSelect}
          onClose={() => setShowTemplateSelector(false)}
        />
      )}

      {showCustomizer && selectedTemplate && (
        <ReportCustomizer
          template={selectedTemplate}
          title={title}
          content={content}
          onCustomize={handleCustomize}
          onExport={handleProfessionalExport}
          onClose={() => setShowCustomizer(false)}
        />
      )}
    </div>
  );
};

export default ReportViewer;
